import { useCallback, useEffect, useRef, useState } from 'react'
import { getFieldRegistration, getFieldValidationAttributes } from '../../utils/formValidation'

function WifiEthernetSection({ register, errors, watch, onBlur, getFieldError, setValue }) {
  const [showNetworkDropdown, setShowNetworkDropdown] = useState(false)
  const [isScanning, setIsScanning] = useState(false)
  const [scanResults, setScanResults] = useState(null)
  const [scanError, setScanError] = useState(null)
  const [currentNetworkInfo, setCurrentNetworkInfo] = useState({ name: '', password: '' })
  const [isLoadingNetworkInfo, setIsLoadingNetworkInfo] = useState(true)
  const [scanProgress, setScanProgress] = useState('')
  
  // Refs for click-outside functionality
  const dropdownRef = useRef(null)
  const scanButtonRef = useRef(null)
  
  const formData = watch()
  const networkType = watch('networkType')

  // Reusable retry fetch function
  const fetchWithRetry = useCallback(async (url, options = {}, config = {}) => {
    const { 
      maxRetries = 2, 
      timeout = 8000, 
      baseDelay = 1000,
      onProgress,
      onError, 
    } = config
    
    let retryCount = 0
    
    while (retryCount <= maxRetries) {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), timeout)
        
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        })
        
        clearTimeout(timeoutId)
        
        if (response.ok) {
          return response
        }
        throw new Error(`HTTP error! Status: ${response.status}`)
      } catch (error) {
        retryCount++
        const isLastAttempt = retryCount > maxRetries
        
        if (!isLastAttempt) {
          const isTimeout = error.name === 'AbortError'
          const message = isTimeout ? 'timed out' : 'failed'
          
          if (onProgress) {
            onProgress(`Attempt ${retryCount} ${message}, retrying in ${baseDelay * retryCount / 1000}s...`)
          }
          
          console.warn(`${url} attempt ${retryCount} ${message}${!isLastAttempt ? ', retrying...' : ''}`)
          await new Promise(resolve => setTimeout(resolve, baseDelay * retryCount))
        } else {
          if (onError) onError(error)
          throw error
        }
      }
    }
  }, [])

  // Fetch current network information on component mount
  useEffect(() => {
    const fetchNetworkInfo = async () => {
      setIsLoadingNetworkInfo(true)
      
      try {
        const response = await fetchWithRetry('/getNetworkInfo', {}, {
          timeout: 10000,
          baseDelay: 2000,
          onError: () => console.error('Failed to fetch network info after all retries'),
        })
        
        const info = await response.json()
        setCurrentNetworkInfo({
          name: info.name || '',
          password: info.password || '',
        })
        
        // Auto-populate network fields with current network info
        if (info.name && setValue) {
          setValue('networkName', info.name)
        }
        if (info.password && setValue) {
          setValue('regulatorPassword', info.password)
        }
      } catch (error) {
        console.error('Network info fetch failed:', error)
        setCurrentNetworkInfo({ name: '', password: '' })
      } finally {
        setIsLoadingNetworkInfo(false)
      }
    }
    
    fetchNetworkInfo()
  }, [setValue, fetchWithRetry])

  // Click-outside handler to close dropdown
  useEffect(() => {
    if (!showNetworkDropdown) return

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target) &&
        scanButtonRef.current && 
        e.target !== scanButtonRef.current
      ) {
        setShowNetworkDropdown(false)
      }
    }

    // Add delay like original HTML implementation
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('click', handleClickOutside)
    }
  }, [showNetworkDropdown])

  const handleFieldBlur = useCallback((fieldName) => (e) => {
    if (onBlur) {
      onBlur(fieldName, e.target.value)
    }
  }, [onBlur])

  const renderFieldError = useCallback((fieldName) => {
    const error = getFieldError ? getFieldError(fieldName) : errors[fieldName]
    return error && <div className="text-danger">{error.message}</div>
  }, [getFieldError, errors])

  // Handle network scanning with enhanced timeout and retry logic
  const handleNetworkScan = useCallback(async () => {
    try {
      setIsScanning(true)
      setScanError(null)
      setScanProgress('Initializing scan...')
      setShowNetworkDropdown(true)
      setScanResults([{ ssid: 'Scanning...', signal: 0 }])

      const response = await fetchWithRetry('/scanNetworks', {}, {
        timeout: 15000,
        onProgress: (message) => setScanProgress(message),
        onError: (error) => {
          if (error.name === 'AbortError') {
            setScanError('Network scan timed out. The device may be busy or network unavailable.')
          } else {
            setScanError('Error scanning networks. Check your connection and try again.')
          }
        },
      })

      setScanProgress('Processing results...')
      const networks = await response.json()
      setScanResults(networks)
      setScanProgress('')
    } catch (error) {
      console.error('Network scan error:', error)
      setScanResults([])
      setScanProgress('')
    } finally {
      setIsScanning(false)
    }
  }, [fetchWithRetry])

  const handleNetworkSelect = useCallback((ssid) => {
    setValue('networkName', ssid)
    setShowNetworkDropdown(false)
  }, [setValue])

  const getSignalIcon = useCallback((signalStrength) => {
    if (signalStrength > 80) return 'bi-wifi'
    if (signalStrength > 60) return 'bi-wifi-2'
    if (signalStrength > 40) return 'bi-wifi-1'
    return 'bi-wifi-off'
  }, [])

  return (
    <div data-component="WifiEthernetSection">
      {/* Wi-Fi / Ethernet Section */}
      <h3 className="mt-3">Wi-Fi / Ethernet</h3>
      <div className="form-label">Network configuration</div>
      
      {/* Network Info Note */}
      <div className="alert alert-info" role="alert">
        <i className="bi bi-info-circle"></i>
        <strong> Note:</strong> Network information is automatically loaded when the page opens. 
        If network scanning times out, the device may be busy or experiencing connectivity issues. 
        Try refreshing the page or waiting a moment before scanning again.
        {!isLoadingNetworkInfo && !currentNetworkInfo.name && !currentNetworkInfo.password && (
          <div className="mt-2">
            <button 
              type="button" 
              className="btn btn-sm btn-outline-info"
              onClick={() => window.location.reload()}
            >
              <i className="bi bi-arrow-clockwise"></i> Retry Loading Network Info
            </button>
          </div>
        )}
      </div>
      <div
        className="btn-group mb-3"
        role="group"
        aria-label="Network type selection"
      >
        <input
          type="radio"
          className="btn-check"
          value="regulatorHosted"
          id="regulatorHosted"
          {...register('networkType', getFieldRegistration('networkType', formData))}
        />
        <label className="btn btn-outline-primary" htmlFor="regulatorHosted">
          Altion hosted Wi-Fi
        </label>
        
        <input
          type="radio"
          className="btn-check"
          value="externalWifi"
          id="externalWifi"
          {...register('networkType', getFieldRegistration('networkType', formData))}
        />
        <label className="btn btn-outline-primary" htmlFor="externalWifi">
          External Wi-Fi
        </label>
        
        <input
          type="radio"
          className="btn-check"
          value="ethernet"
          id="ethernet"
          {...register('networkType', getFieldRegistration('networkType', formData))}
        />
        <label className="btn btn-outline-primary" htmlFor="ethernet">
          No Wi-Fi / Ethernet only
        </label>
      </div>
      
      {errors.networkType && (
        <div className="text-danger mb-3">{renderFieldError('networkType')}</div>
      )}

      {/* Wi-Fi Details - only show if not ethernet-only */}
      {networkType !== 'ethernet' && (
        <div id="wifiDetails">
          <div className="mb-3">
            <label htmlFor="networkName" className="form-label">
              Network name
            </label>
            <div className="input-group">
              <input
                type="text"
                className={`form-control ${getFieldError && getFieldError('networkName') ? 'is-invalid' : ''}`}
                id="networkName"
                {...getFieldValidationAttributes('networkName', formData)}
                {...register('networkName', getFieldRegistration('networkName', formData))}
                onBlur={handleFieldBlur('networkName')}
              />
              {networkType === 'externalWifi' && (
                <button
                  ref={scanButtonRef}
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={handleNetworkScan}
                  disabled={isScanning}
                >
                  {isScanning ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      {scanProgress || 'Scanning...'}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-wifi"></i> Scan
                    </>
                  )}
                </button>
              )}
            </div>
            {renderFieldError('networkName')}
            
            {/* Network scan results dropdown */}
            {showNetworkDropdown && (
              <div ref={dropdownRef} className="list-group mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {scanError ? (
                  <div className="list-group-item text-center text-danger">
                    {scanError}
                  </div>
                ) : isScanning ? (
                  <div className="list-group-item text-center">
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Scanning...
                  </div>
                ) : scanResults?.length === 0 ? (
                  <div className="list-group-item text-center">
                    No networks found
                  </div>
                ) : (
                  scanResults?.map((network, index) => (
                    <button
                      key={`${network.ssid}-${network.bssid || index}`}
                      type="button"
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                      onClick={() => handleNetworkSelect(network.ssid)}
                    >
                      {network.ssid}
                      <i className={`bi ${getSignalIcon(network.signal)}`}></i>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="mb-3">
            <label htmlFor="regulatorPassword" className="form-label">
              Network password
            </label>
            <input
              type="text"
              className={`form-control ${getFieldError && getFieldError('regulatorPassword') ? 'is-invalid' : ''}`}
              id="regulatorPassword"
              {...getFieldValidationAttributes('regulatorPassword', formData)}
              {...register('regulatorPassword', getFieldRegistration('regulatorPassword', formData))}
              onBlur={handleFieldBlur('regulatorPassword')}
            />
            {renderFieldError('regulatorPassword')}
          </div>
        </div>
      )}
    </div>
  )
}

export default WifiEthernetSection
